import { ProjectTypes } from '@platform/types';
import { Helpers } from '@platform/utils';
import * as turf from '@turf/turf';
import { hex, valid } from 'chroma-js';
import crc32 from 'crc/crc32';
import * as GeoJSON from 'geojson';
import maplibregl, {
  CircleLayerSpecification,
  ColorSpecification,
  DataDrivenPropertyValueSpecification,
  ExpressionFilterSpecification,
  ExpressionSpecification,
  FillLayerSpecification,
  HeatmapLayerSpecification,
  LineLayerSpecification,
  LngLat,
  Map,
  StyleSpecification,
} from 'maplibre-gl';
import { RuleGroupType, RuleType } from 'react-querybuilder';

// type AllowedLayerSpecification =
//   | CircleLayerSpecification
//   | FillLayerSpecification
//   | LineLayerSpecification
//   | SymbolLayerSpecification
//   | HeatmapLayerSpecification;

export const DEFAULT_OPACITIES = [0.5, 0.5];

export const getWorkingLayerFingerprint = (workingLayer: ProjectTypes.AnyWorkingLayer) => {
  const { id, source, filter, metadata, variableSelection, tooltip } = workingLayer;
  return `${crc32(JSON.stringify({ id, metadata, source, filter, variableSelection, tooltip }))}`;
};

export const getWorkingLayerId = (workingLayer: ProjectTypes.AnyWorkingLayer) =>
  `STYLE-SPEC:${getWorkingLayerFingerprint(workingLayer)}:${workingLayer.id}`;

export const mergeCanvases = (
  canvases: HTMLCanvasElement[],
  mode: 'single' | 'side' | 'side-locked' | 'swipe'
): HTMLCanvasElement => {
  // Create a new canvas
  const mergedCanvas = document.createElement('canvas');
  const ctx = mergedCanvas.getContext('2d');
  if (!ctx) {
    throw new Error('Unable to get 2D context');
  }

  const DIVIDER_WIDTH = 2;
  switch (mode) {
    case 'side':
    case 'side-locked':
      mergedCanvas.width = canvases.reduce((p, c) => p + c.width, 0) + canvases.length * DIVIDER_WIDTH;
      break;
    case 'single':
    default:
      mergedCanvas.width = Math.max(...canvases.map((c) => c.width));
      break;
  }

  mergedCanvas.height = Math.max(...canvases.map((c) => c.height));

  // Set the fill color
  ctx.fillStyle = 'rgb(51,65,85)';

  // Draw a rectangle that covers the entire canvas
  ctx.fillRect(0, 0, mergedCanvas.width, mergedCanvas.height);

  canvases.forEach((c, idx) => {
    if (mode === 'side' || mode === 'side-locked') {
      const dX = idx * (c.width + DIVIDER_WIDTH);
      ctx.drawImage(c, dX, 0);
    } else if (mode === 'swipe') {
      if (idx === 0) {
        ctx.drawImage(c, 0, 0);
      } else {
        const dX = c.width / (idx + 1);
        ctx.drawImage(c, dX, 0, dX, c.height, dX, 0, dX, mergedCanvas.height);
        // Draw a rectangle that covers the entire canvas
        ctx.fillRect(dX, 0, DIVIDER_WIDTH, mergedCanvas.height);
      }
    } else {
      // single
      ctx.drawImage(c, 0, 0);
    }
  });

  return mergedCanvas;
};

export const getThumbCanvas = async (map: Map): Promise<HTMLCanvasElement | null> =>
  new Promise((resolve) => {
    const getCanvas = (map: Map) => {
      try {
        const canvas = map.getCanvas();
        const newCanvas = document.createElement('canvas');

        newCanvas.width = canvas.width;
        newCanvas.height = canvas.height;

        const ctx = newCanvas.getContext('2d');
        ctx?.drawImage(canvas, 0, 0);

        return newCanvas;
      } catch {
        console.warn("Failed to create dataUrl of map's canvas");
        return null;
      }
    };
    if (!map) {
      console.warn('There is no map. Returning empty canvas');
      return null;
    }

    if (map.isStyleLoaded()) {
      resolve(getCanvas(map));
    } else {
      const handleMapIdle = () => {
        map.off('idle', handleMapIdle);
        resolve(getCanvas(map));
      };
      map.on('idle', handleMapIdle);
    }
  });

export const getPaintStops = (
  r: ProjectTypes.CutPointSet
): [number | undefined | string, string | undefined, boolean][] => {
  const paintStops: [number | undefined | string, string | undefined, boolean][] = [];

  if (r.insufficientData) {
    paintStops.push([undefined, toCSSColor(r.insufficientData.color), false]);
  }
  r.cutPoints.forEach((cp) => {
    paintStops.push(
      cp.to != null && cp.from != null
        ? [cp.to, toCSSColor(cp.color), cp.to === cp.from]
        : [undefined, toCSSColor(cp.color), cp.to === cp.from]
    );
  });

  return paintStops;
};

export const toCSSColor = (value: string | undefined, alpha = 1) => {
  if (!value || !valid(value)) return undefined;

  try {
    return hex(value).alpha(alpha).css();
  } catch {
    return value;
  }
};

export const calculateEquidistantSteps = (values: number[], maxNumberOfBuckets: number): number[] => {
  const sortedValues = values.sort((a, b) => a - b);

  /**
   *  If the number of values in the input array is less than or equal to the specified,
   * then return sorted array
   * */
  if (values.length <= maxNumberOfBuckets) {
    return sortedValues;
  }

  /**
   * In this step, the initial centroids are generated. It divides the sorted values
   * into maxNumberOfBuckets equidistant intervals and selects a value from each
   *  interval as a centroid.
   */
  let centroids: number[] = [];
  for (let i = 0; i < maxNumberOfBuckets; i++) {
    const index = Math.floor((i / (maxNumberOfBuckets - 1)) * (sortedValues.length - 1));
    centroids.push(sortedValues[index]);
  }

  /**
   * This loop does the K-Means clustering process.
   * In each iteration, the centroids are recalculated
   * based on the current assignment of values to clusters.
   * For each value in the sorted input values, the code iterates
   * through the current centroids to find the one with the minimum
   * distance to the value. It assigns the value to the cluster associated
   * with that centroid.
   * */
  for (let iteration = 0; iteration < maxNumberOfBuckets; iteration++) {
    const clusters: number[][] = Array.from({ length: maxNumberOfBuckets }, () => []);

    for (const value of sortedValues) {
      let minDistance = Infinity;
      let clusterIndex = 0;

      for (let i = 0; i < centroids.length; i++) {
        const distance = Math.abs(value - centroids[i]);
        if (distance < minDistance) {
          minDistance = distance;
          clusterIndex = i;
        }
      }

      clusters[clusterIndex].push(value);
    }

    /**
     * After all values are assigned to clusters, new centroids are calculated by
     * taking the mean of the values in each cluster. This step repositions
     * the centroids based on the current assignments.
     * */
    const newCentroids = clusters.map((cluster) => cluster.reduce((a, b) => a + b, 0) / cluster.length);

    /**
     * The newly calculated centroids are updated with a fixed precision of 5
     * decimal places to avoid floating-point precision issues. This precision
     * control helps ensure that the centroids converge to stable values.
     */
    centroids = newCentroids.map((centroid) => parseFloat(centroid.toFixed(5)));
  }

  return centroids;
};

export const getMapStyle = (styleSpec: ProjectTypes.MapSpec): StyleSpecification => {
  const mapStyle: StyleSpecification = { sources: {}, layers: [], version: 8 };

  const layers = [...styleSpec.workingLayers];

  if (layers.length) {
    // const workingLayersToAdd: AllowedLayerSpecification[] = [];

    layers.forEach((workingLayer) => {
      const fingerprint = getWorkingLayerFingerprint(workingLayer);
      const source = `STYLE-SPEC:${fingerprint}:${workingLayer.id}`;
      // define source
      mapStyle.sources[source] = {
        type: 'vector',
        tiles: [`${workingLayer.metadata.tiles}&${source}`],
      };

      const layerFilters: ExpressionFilterSpecification = ['all'];

      if (workingLayer.filter) {
        const filterToApply = ruleGroupToMapLibreFilter(workingLayer.filter);
        if (filterToApply) {
          layerFilters.push(filterToApply);
        }
      }

      // TODO: test selection masking
      if (workingLayer.maskingSelection.features.length && workingLayer.maskingSelection.doMask) {
        const geoFilter: RuleGroupType = {
          combinator: 'OR',
          rules: workingLayer.maskingSelection.features.map((fs) => ({
            value: fs.id,
            field: workingLayer.metadata.identifiers.row.variableId,
            operator: '=',
          })),
        };

        const geoFilterToApply = ruleGroupToMapLibreFilter(geoFilter);
        if (geoFilterToApply) {
          layerFilters.push(geoFilterToApply);
        }
      }

      const commonSpec: Partial<CircleLayerSpecification | FillLayerSpecification | LineLayerSpecification> = {
        id: getWorkingLayerId(workingLayer),
        source: source,
        'source-layer': workingLayer.metadata.geoLayerId,
        layout: {
          visibility: workingLayer.visible ? 'visible' : 'none',
        },
        filter: layerFilters,
      };

      if (workingLayer.type === ProjectTypes.LayerType.POINT) {
        // TODO: merge workingLayer.type with rendering type?????????
        const { opacities, baseColor } = workingLayer.rendering.style as ProjectTypes.SimpleRenderingStyle;
        const [circleOpacity, strokeOpacity] = opacities;

        switch (workingLayer.rendering.type) {
          case 'heatmap':
            mapStyle.layers.push({
              ...commonSpec,
              type: 'heatmap',
              paint: {
                'heatmap-intensity': 0.5,
              },
            } as HeatmapLayerSpecification);
            break;
          case 'simple':
            mapStyle.layers.push({
              ...commonSpec,
              type: 'circle',
              paint: {
                'circle-opacity': circleOpacity,
                'circle-stroke-opacity': ['case', ['boolean', ['feature-state', 'highlight'], false], 1, strokeOpacity],
                'circle-stroke-width': ['case', ['boolean', ['feature-state', 'highlight'], false], 2, 1],
                'circle-radius': ['case', ['boolean', ['feature-state', 'highlight'], false], 6, 4],
                'circle-stroke-color': '#fff',
                'circle-color': [
                  'case',
                  ['boolean', ['feature-state', 'highlight'], false],
                  'rgb(79, 70, 229)',
                  baseColor,
                ],
              },
            } as CircleLayerSpecification);
            break;
        }

        if (workingLayer.maskingSelection.features.length) {
          const { identifiers } = workingLayer.metadata;
          const { row } = identifiers;

          mapStyle.layers.push({
            ...commonSpec,
            id: `${source}-selection`,
            type: 'circle',
            filter: ['in', row.variableId, ...workingLayer.maskingSelection.features.map((x) => x.id)],
            paint: {
              'circle-color': 'red',
            },
          } as CircleLayerSpecification);
        }
      } else if (workingLayer.type === ProjectTypes.LayerType.POLYGON) {
        switch (workingLayer.rendering.type) {
          case 'shaded':
            {
              const { variableSelection = [] } = workingLayer;

              const { opacities, cutPointSets } = workingLayer.rendering.style as ProjectTypes.ShadedRenderingStyle;

              const MISSING_DATA_COLOR = 'rgb(125,125,125)';
              const DEFAULT_COLOR = 'rgb(125,125,125)';

              const casesPerRendering = variableSelection.map((x, idx) => {
                const isComputed = x.universeVariableId != null;
                const value = isComputed
                  ? ['/', ['get', x.variableId], ['get', x.universeVariableId]]
                  : ['get', x.variableId];

                const colorStops = getPaintStops(cutPointSets[idx]);

                const paintFillColorCases: unknown[] = colorStops.reduce((p: unknown[], c) => {
                  const [stopValue, stopColor, isEqualOperator] = c;

                  if (stopValue == null) {
                    return [...p, ['==', ['get', x.variableId], null], stopColor];
                  }
                  if (isEqualOperator) {
                    return [...p, ['==', ['get', x.variableId], stopValue], stopColor];
                  }

                  return [...p, ['<=', value, isComputed ? Number(stopValue) / 100 : stopValue], stopColor];
                }, []);

                paintFillColorCases.push(MISSING_DATA_COLOR);
                return ['case', ...paintFillColorCases];
              });

              const fillColorExpression =
                variableSelection.length === 1
                  ? casesPerRendering[0]
                  : generatePropertyComparisonExpression(
                      variableSelection.map((x) => x.variableId),
                      casesPerRendering,
                      MISSING_DATA_COLOR,
                      DEFAULT_COLOR
                    );

              mapStyle.layers.push({
                ...commonSpec,
                id: `${source}`,
                type: 'fill',
                paint: {
                  'fill-color': [
                    'case',
                    ['boolean', ['feature-state', 'highlight'], false],
                    'rgb(79, 70, 229)',
                    ['boolean', ['feature-state', 'hover'], false],
                    'rgb(79, 70, 229)',
                    fillColorExpression as DataDrivenPropertyValueSpecification<ColorSpecification>,
                  ],
                  'fill-opacity': [
                    'case',
                    ['boolean', ['feature-state', 'highlight'], false],
                    0.75,
                    ['boolean', ['feature-state', 'hover'], false],
                    0.65,
                    opacities[0],
                  ],
                  'fill-outline-color': 'rgba(0,0,0,0)',
                },
              } as FillLayerSpecification);

              // mapStyle.layers.push({
              //   ...commonSpec,
              //   id: `${source}-centroid`,
              //   type: 'circle',
              //   'source-layer': `${commonSpec['source-layer']}p`,
              //   paint: {
              //     'circle-opacity': 0.3,
              //     'circle-radius': 1,
              //     'circle-color': 'red',
              //   },
              // } as CircleLayerSpecification);

              // TODO: filtered out
              mapStyle.layers.push({
                ...commonSpec,
                id: `${source}-filtered-out`,
                type: 'fill',
                paint: {
                  'fill-pattern': [
                    'case',
                    [
                      'all',
                      ...variableSelection.reduce(
                        (p: unknown[], c) => [
                          ...p,
                          ['==', ['get', c.variableId], null],
                          ['==', ['get', c.variableId], NaN],
                        ],
                        []
                      ),
                    ],
                    'gray_pattern',
                    'pattern',
                  ] as DataDrivenPropertyValueSpecification<string>,
                  'fill-opacity': 0.85,
                },
                filter: ['!', commonSpec.filter as ExpressionSpecification],
              } as FillLayerSpecification);

              mapStyle.layers.push({
                ...commonSpec,
                id: `${source}-line`,
                type: 'line',
                paint: {
                  'line-color': '#FFF',
                  'line-opacity': opacities[1],
                  'line-width': 1,
                },
                // filter: ['all'] // TODO:
              } as LineLayerSpecification);
            }

            break;
          case 'simple':
            {
              const { opacities, baseColor } = workingLayer.rendering.style as ProjectTypes.SimpleRenderingStyle;

              mapStyle.layers.push({
                ...commonSpec,
                id: `${source}`,
                type: 'fill',
                paint: {
                  'fill-opacity': [
                    'case',
                    ['boolean', ['feature-state', 'highlight'], false],
                    0.75,
                    ['boolean', ['feature-state', 'hover'], false],
                    0.65,
                    opacities[0],
                  ],
                  'fill-color': [
                    'case',
                    ['boolean', ['feature-state', 'highlight'], false],
                    'rgb(79, 70, 229)',
                    ['boolean', ['feature-state', 'hover'], false],
                    'rgb(79, 70, 229)',
                    baseColor,
                  ],
                },
              } as FillLayerSpecification);

              mapStyle.layers.push({
                ...commonSpec,
                id: `${source}-${fingerprint}-line`,
                type: 'line',
                paint: {
                  'line-color': baseColor,
                  'line-opacity': opacities[1],
                  'line-width': 2,
                },
              } as LineLayerSpecification);
            }
            break;
          default:
            console.error('NOT HANDLED', workingLayer);
        }

        if (workingLayer.maskingSelection.features.length) {
          const { identifiers } = workingLayer.metadata;
          const { row } = identifiers;
          mapStyle.layers.push({
            ...commonSpec,
            id: `${source}-masking`,
            type: 'line',
            filter: ['in', row.variableId, ...workingLayer.maskingSelection.features.map((x) => x.id)],
            paint: {
              'line-width': 2,
              'line-color': 'rgb(79, 70, 229)',
              'line-opacity': 1,
            },
          } as LineLayerSpecification);
        }

        // TODO: probably not needed
        // if (workingLayer.featureSelection.length) {
        //   const { identifiers } = workingLayer.metadata;
        //   const { row } = identifiers;
        //   mapStyle.layers.push({
        //     ...commonSpec,
        //     id: `${source}-selection`,
        //     type: 'line',
        //     filter: ['in', row.variableId, ...workingLayer.featureSelection.map((x) => x.id)],
        //     paint: {
        //       'line-width': 2,
        //       'line-color': 'rgb(147,4,33)',
        //       'line-opacity': 1,
        //     },
        //   } as LineLayerSpecification);
        // }
      } else {
        // skip as it is unknown or not handled yet
        console.warn(`${workingLayer.id} skipped due to unsupported type ${workingLayer.type}`);
      }
    });
  }

  if (styleSpec.analysis) {
    if (styleSpec.analysis.type === 'geo') {
      const source = `STYLE-SPEC:analysis`;
      const layerId = `STYLE-SPEC:analysis-layer`;

      const { datasetId, rowIdentifier, nameIdentifier, geoLayerId } = styleSpec.analysis.layer;

      mapStyle.sources[source] = {
        type: 'vector',
        tiles: [
          `${process.env.NX_SOCEX_TILES_SERVER}?x={x}&y={y}&z={z}&layers=${geoLayerId}&columns=${geoLayerId}.${datasetId}.${rowIdentifier},${geoLayerId}.${datasetId}.${nameIdentifier}&projection=EPSG-3857`,
        ],
      };

      mapStyle.layers.push({
        id: `${layerId}-selection`,
        source: source,
        'source-layer': geoLayerId,
        layout: {
          visibility: 'visible',
        },
        paint: {
          'line-color': 'rgb(125,125,255)',
          'line-opacity': 1,
          'line-width': 4,
        },
        filter: ['in', rowIdentifier, ...styleSpec.analysis.selection.map((s) => s.id)],
        type: 'line',
      });
    } else {
      console.info(`Unhandled analysis type '${styleSpec.analysis.type}'`);
    }
  }

  return mapStyle;
};

const generatePropertyComparisonExpression = (
  properties: string[],
  renderingColorExpressions: unknown[],
  missingColor: string,
  defaultColor: string
): unknown[] => {
  const maxExpression = [
    'max',
    ...properties.map((property) => ['coalesce', ['get', property], Number.MIN_SAFE_INTEGER]),
  ];

  const colorCases = properties.map((property, index) => [
    ['==', ['get', property], maxExpression],
    renderingColorExpressions[index],
  ]);

  return [
    'case',
    ['all', ...properties.reduce((p: unknown[], c) => [...p, ['==', ['get', c], null], ['==', ['get', c], NaN]], [])],
    missingColor,
    ...colorCases.flat(),
    defaultColor,
  ];
};

const ruleGroupToMapLibreFilter = (ruleGroup: RuleGroupType): ExpressionSpecification | null => {
  const filters = ruleGroup.rules
    .filter((rule) => Helpers.checkIfValidRule(rule, null))
    .map((rule) => {
      if ((rule as RuleType).operator) {
        return ruleToMapLibreFilter(rule as RuleType);
      } else if ((rule as RuleGroupType).rules && (rule as RuleGroupType).rules.length > 0) {
        return ruleGroupToMapLibreFilter(rule as RuleGroupType);
      } else {
        return true;
      }
    });

  if (!filters.length) return null;

  let operator: 'all' | 'any';

  switch (ruleGroup.combinator.toLowerCase()) {
    case 'or':
      operator = 'any';
      break;
    case 'and':
    default:
      operator = 'all';
      break;
  }

  return [operator, ...filters] as ExpressionSpecification;
};

function ruleToMapLibreFilter(rule: ProjectTypes.WorkingLayerFilterRuleTypeBase): ExpressionSpecification {
  const ruleField = rule.field;
  const ruleValue = rule.value.value;

  if (rule.value.type === 'pct') {
    return [
      rule.operator as any,
      ['/', ['get', ruleField], ['get', rule.value.universeVariableId ?? '']],
      ruleValue != null ? (ruleValue as number) / 100 : ruleValue,
    ];
  }

  switch (rule.operator) {
    case 'contains':
      return [
        'match',
        ['index-of', ['downcase', String(ruleValue)], ['downcase', ['get', ruleField]]],
        -1,
        false,
        true,
      ];
    case 'beginsWith':
      // use "slice" to extract the first N characters (N = rule.value.value.length) and compare it to the actual rule.value
      return [
        '==',
        ['downcase', ['slice', ['get', ruleField], 0, ['length', String(ruleValue)]]],
        ['downcase', String(ruleValue)],
      ];
    case 'endsWith':
      // use "slice" to extract the last N characters (N  = rule.value.value.length * -1) and compare it to the actual rule.value
      return [
        '==',
        ['downcase', ['slice', ['get', ruleField], ['*', ['length', String(ruleValue)], -1]]],
        ['downcase', String(ruleValue)],
      ];
    case 'null':
      return ['!', ['has', ruleField]];
    case 'notNull':
      return ['has', ruleField];
    case '=':
    case '==':
      return ['==', ['get', ruleField], ruleValue];
    case '!=':
      return ['!=', ['get', ruleField], ruleValue];
    case '>':
      return ['>', ['coalesce', ['get', ruleField], Number.MAX_SAFE_INTEGER], ruleValue];
    case '>=':
      return ['>=', ['coalesce', ['get', ruleField], Number.MAX_SAFE_INTEGER], ruleValue];
    case '<':
      return ['<', ['coalesce', ['get', ruleField], Number.MIN_SAFE_INTEGER], ruleValue];
    case '<=':
      return ['<=', ['coalesce', ['get', ruleField], Number.MIN_SAFE_INTEGER], ruleValue];
    case 'between': {
      const [from, to] = String(ruleValue).split(',');
      return ['all', ['>=', ['get', ruleField], parseFloat(from)], ['<=', ['get', ruleField], parseFloat(to)]];
    }
    default:
      return ['all'];
  }
}

export const extractFieldsFromRuleGroup = (ruleGroup: ProjectTypes.WorkingLayerFilterRuleGroupBase): string[] => {
  const fields: string[] = [];

  const extractFields = (group: RuleGroupType) => {
    group.rules
      .filter((rule) => Helpers.checkIfValidRule(rule, null))
      .forEach((ruleOrGroup) => {
        if ((ruleOrGroup as ProjectTypes.WorkingLayerFilterRuleTypeBase).field) {
          const rule = ruleOrGroup as ProjectTypes.WorkingLayerFilterRuleTypeBase;
          fields.push(rule.field);
          if (rule.value.universeVariableId != null) {
            fields.push(rule.value.universeVariableId);
          }
        } else {
          extractFields(ruleOrGroup as RuleGroupType);
        }
      });
  };

  extractFields(ruleGroup);
  return fields;
};

export const calculateBounds = (
  featureCollection: GeoJSON.FeatureCollection<GeoJSON.Geometry>
): maplibregl.LngLatBoundsLike | null => {
  if (!featureCollection.features.length) return null;

  const [a, b, c, d] = turf.bbox(featureCollection);
  return [a, b, c, d];
};

// Helper function to normalize LngLatBoundsLike to a consistent format
const normalizeBounds = (bounds: maplibregl.LngLatBoundsLike): [number, number, number, number] => {
  if (Array.isArray(bounds) && bounds.length === 2) {
    const sw = bounds[0];
    const ne = bounds[1];
    const west = Array.isArray(sw) ? sw[0] : (sw as LngLat).lng;
    const south = Array.isArray(sw) ? sw[1] : sw.lat;
    const east = Array.isArray(ne) ? ne[0] : (sw as LngLat).lng;
    const north = Array.isArray(ne) ? ne[1] : ne.lat;
    return [west, south, east, north];
  } else if (Array.isArray(bounds) && bounds.length === 4) {
    return bounds;
  }
  // Add more cases if necessary, e.g., for LngLatBounds instances
  throw new Error('Unsupported bounds format');
};

export const calculateCenterOfBoundsLike = (boundsLike: maplibregl.LngLatBoundsLike): maplibregl.LngLatLike => {
  const [west, south, east, north] = normalizeBounds(boundsLike);
  const centerLng = (west + east) / 2;
  const centerLat = (south + north) / 2;
  return [centerLng, centerLat]; // Or { lng: centerLng, lat: centerLat } for object format
};
